import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  List,
  ListInput,
  ListMembersBody,
  ListPatch,
  PaginatedResponse,
} from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const LISTS_KEY = "lists" as const;
const CONTACTS_KEY = "contacts" as const;

export type ListResponse = List;
export type ListsPage = PaginatedResponse<List>;

export function useLists() {
  return useQuery<ListsPage, ApiClientError>({
    queryKey: [LISTS_KEY, "list"],
    queryFn: () => apiFetch<ListsPage>("/lists"),
  });
}

export function useList(id: string | undefined) {
  return useQuery<ListResponse, ApiClientError>({
    queryKey: [LISTS_KEY, "detail", id],
    queryFn: () => apiFetch<ListResponse>(`/lists/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation<ListResponse, ApiClientError, ListInput>({
    mutationFn: (body) =>
      apiFetch<ListResponse>("/lists", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (list) => {
      qc.setQueryData([LISTS_KEY, "detail", list.id], list);
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "list"] });
    },
  });
}

export function useUpdateList(id: string) {
  const qc = useQueryClient();
  return useMutation<ListResponse, ApiClientError, ListPatch>({
    mutationFn: (body) =>
      apiFetch<ListResponse>(`/lists/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (list) => {
      qc.setQueryData([LISTS_KEY, "detail", list.id], list);
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "list"] });
    },
  });
}

export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/lists/${id}`, { method: "DELETE" }),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: [LISTS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "list"] });
      // The deleted scope is no longer valid — drop any cached contact
      // lists that may have been keyed under it.
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
    },
  });
}

export function useAddListMembers(listId: string) {
  const qc = useQueryClient();
  return useMutation<ListResponse, ApiClientError, ListMembersBody>({
    mutationFn: (body) =>
      apiFetch<ListResponse>(`/lists/${listId}/members`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (list) => {
      qc.setQueryData([LISTS_KEY, "detail", list.id], list);
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
    },
  });
}

export function useRemoveListMembers(listId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, ListMembersBody>({
    mutationFn: (body) =>
      apiFetch<void>(`/lists/${listId}/members`, {
        method: "DELETE",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "detail", listId] });
      qc.invalidateQueries({ queryKey: [LISTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
    },
  });
}
